import React from 'react';
import CloseRounded from '@mui/icons-material/CloseRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ConnectionUpdateIcon from './ConnectionUpdateIcon';

type ConnectionUpdateBannerProps = {
  title: string;
  description?: string;
  size?: 'small' | 'medium' | 'large';
  updateLabel?: string;
  onUpdate: () => void;
  onDismiss?: () => void;
  learnMoreUrl?: string;
  onLearnMore?: () => void;
};

export default function ConnectionUpdateBanner({
  title,
  description,
  size = 'large',
  updateLabel = 'Update now',
  onUpdate,
  onDismiss,
  learnMoreUrl,
  onLearnMore,
}: ConnectionUpdateBannerProps): React.JSX.Element {
  const compact = size !== 'large';
  return (
    <Box
      sx={{
        position: 'relative',
        '& .connection-update-dismiss': { opacity: 0 },
        '&:hover .connection-update-dismiss, &:focus-within .connection-update-dismiss': { opacity: 1 },
        '@media (hover: none)': { '& .connection-update-dismiss': { opacity: 1 } },
      }}
    >
      <Paper
        data-testid='connection-update-banner'
        sx={{
          bgcolor: 'primary.light',
          px: compact ? 1.5 : 2,
          pl: size === 'small' ? 1 : undefined,
          py: size === 'small' ? 1.5 : 2,
          borderRadius: compact ? 'lg' : 'xl',
        }}
      >
        <Stack
          direction={{ xs: compact ? 'row' : 'column', sm: 'row' }}
          alignItems='center'
          sx={{
            columnGap: size === 'small' ? 1.5 : compact ? 3 : 0,
            rowGap: 2,
            flexWrap: compact ? undefined : 'wrap',
            justifyContent: compact ? undefined : 'center',
          }}
        >
          <Stack
            direction='row'
            alignItems='flex-start'
            gap={1}
            flex={1}
            minWidth={0}
            sx={compact ? undefined : { width: '100%', maxWidth: '100%', flexBasis: { xs: 'auto', sm: 360 } }}
          >
            <Box sx={{ display: 'flex', p: compact ? 0 : 0.25 }}>
              <ConnectionUpdateIcon variant={compact ? 'default' : 'banner'} />
            </Box>
            <Stack gap={0.5} flex={1} sx={compact ? undefined : { minWidth: 0, overflowWrap: 'anywhere' }}>
              <Typography variant={compact ? 'captionRegular' : 'bodyMedium'}>{title}</Typography>
              {description && (
                <Typography variant='paragraphRegular' color='text.secondary'>
                  {description}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Stack
            direction='row'
            alignItems='center'
            gap={1.5}
            flexShrink={0}
            sx={compact ? undefined : { maxWidth: '100%', flexWrap: 'wrap', justifyContent: 'center' }}
          >
            {learnMoreUrl && (
              <Button
                href={learnMoreUrl}
                target='_blank'
                rel='noopener noreferrer'
                size='medium'
                onClick={onLearnMore}
                endIcon={<OpenInNewRounded />}
                sx={{
                  px: (theme) => `calc(${theme.spacing(2)} - 1px)`,
                  '& .MuiButton-endIcon': { mr: 0 },
                }}
              >
                Learn more
              </Button>
            )}
            <Button
              variant={compact ? 'outlined' : 'contained'}
              size={compact ? 'small' : 'medium'}
              sx={
                compact
                  ? {
                      px: (theme) => `calc(${theme.spacing(1)} - 1px)`,
                      minWidth: size === 'small' ? 66 : 97,
                    }
                  : { px: (theme) => `calc(${theme.spacing(2)} - 1px)` }
              }
              onClick={onUpdate}
            >
              {updateLabel}
            </Button>
          </Stack>
        </Stack>
      </Paper>
      {onDismiss && (
        <IconButton
          aria-label='Dismiss connection update'
          className='connection-update-dismiss'
          variant='outlined'
          size='small'
          onClick={onDismiss}
          sx={{ position: 'absolute', top: -16, right: -16, boxShadow: 12, transition: 'opacity 150ms' }}
        >
          <CloseRounded fontSize='inherit' />
        </IconButton>
      )}
    </Box>
  );
}
